/**
 * Batch 18-D4 — Topic Cluster Classification.
 *
 * Pure helper that maps an artifact to one of 12 hardcoded topic
 * clusters (or to a domain-based / `'general'` fallback). The result
 * is used by the recommendation engine to populate
 * `ScoredArtifact.topicCluster`, which the guardrail module
 * (`@/utils/training/guardrails/caps`) then reads to enforce the
 * "max 1 per cluster" cap.
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/trainingRecommendationEngine.ts` prior to Batch 18-D4:
 *
 *   - The `TOPIC_CLUSTERS` table is moved verbatim. All 12 cluster
 *     keys and every keyword list are unchanged.
 *   - The classifier compares lower-cased substrings (.includes())
 *     against the joined search-text. This is intentionally NOT a
 *     word-boundary match — preserved as-is.
 *   - Cluster iteration order follows JavaScript's insertion order
 *     over `Object.entries(TOPIC_CLUSTERS)`. First match wins. The
 *     classifier returns the first cluster whose keyword list has
 *     any substring hit in the search-text.
 *   - Fallback when no cluster matches: the first domain's
 *     `domain_key`, then `'general'`.
 *
 * The function signature accepts a narrow `TopicClusterArtifactInput`
 * that exposes only the three fields the classifier actually reads.
 * This decouples the helper from the engine's private
 * `ArtifactWithDomains` shape while remaining structurally
 * satisfiable by any `ArtifactWithDomains` value (so the engine's
 * existing call site at `scoreArtifacts` continues to compile
 * without modification).
 *
 * Adding a new cluster, renaming an existing key, or changing the
 * matcher (e.g. word-boundary vs substring) is a behavior change
 * the guardrail module will silently observe through the
 * `max 1 per cluster` cap. Run the 18-D4 behavior-pin tests after
 * any such edit.
 */

// ─────────────────────────────────────────────────────────────────────
// Input shape
//
// Narrowed to exactly the fields the classifier reads. Any wider
// type that includes these three fields (e.g. the engine's private
// `ArtifactWithDomains`) is assignable to this input via TypeScript's
// structural typing.
// ─────────────────────────────────────────────────────────────────────

export interface TopicClusterArtifactInput {
  title: string;
  primary_skill?: string | null;
  domains?: Array<{
    domain_key: string;
    // Additional fields (is_primary, relevance_score) are accepted via
    // structural typing — declaring just `domain_key` keeps this input
    // independent of the engine's domain-row shape.
  }>;
}

// ─────────────────────────────────────────────────────────────────────
// Cluster table
//
// Moved verbatim from `trainingRecommendationEngine.ts` (Batch 18-D2
// landing state). Order of keys is load-bearing — the classifier
// returns the FIRST matching cluster. Changing key order changes the
// classification result for artifacts whose search-text hits multiple
// cluster keywords.
// ─────────────────────────────────────────────────────────────────────

export const TOPIC_CLUSTERS: Record<string, string[]> = {
  fuel_systems: ['fuel_trim', 'fuel_injection', 'fuel_pump', 'injector', 'fuel_pressure'],
  ignition: ['spark', 'coil', 'ignition', 'misfire', 'timing'],
  sensors: ['oxygen_sensor', 'o2', 'maf', 'map', 'tps', 'cts', 'iat', 'transducer'],
  emissions: ['evap', 'catalytic', 'egr', 'emissions', 'smog'],
  electrical: ['wiring', 'circuit', 'voltage_drop', 'ground', 'relay', 'fuse'],
  diagnostics: ['scan_tool', 'pid', 'mode_6', 'freeze_frame', 'dtc', 'obd'],
  drivability: ['hesitation', 'stall', 'surge', 'rough_idle', 'no_start'],
  network: ['can_bus', 'lin_bus', 'communication', 'module', 'network'],
  hvac: ['ac', 'heating', 'climate', 'refrigerant', 'compressor'],
  brakes: ['abs', 'brake', 'hydraulic', 'rotor', 'caliper'],
  transmission: ['trans', 'shift', 'torque_converter', 'clutch', 'gear'],
  engine_mechanical: ['compression', 'timing_chain', 'valve', 'camshaft', 'crankshaft'],
};

// ─────────────────────────────────────────────────────────────────────
// Classifier
//
// Body is verbatim from the engine. Search-text composition:
//   [title (lower), primary_skill (lower or ''), ...domain_keys (lower)]
//   .join(' ')
// then linear scan over `Object.entries(TOPIC_CLUSTERS)`.
// ─────────────────────────────────────────────────────────────────────

/**
 * Determine the topic cluster for an artifact based on title and domains
 */
export function getTopicCluster(artifact: TopicClusterArtifactInput): string {
  const searchText = [
    artifact.title.toLowerCase(),
    artifact.primary_skill?.toLowerCase() || '',
    ...(artifact.domains?.map(d => d.domain_key.toLowerCase()) || []),
  ].join(' ');

  for (const [cluster, keywords] of Object.entries(TOPIC_CLUSTERS)) {
    if (keywords.some(keyword => searchText.includes(keyword))) {
      return cluster;
    }
  }

  // Default to primary domain or 'general'
  return artifact.domains?.[0]?.domain_key || 'general';
}
